import { inflect } from 'inflection'

import { type BulkUploadMode, MAX_BULK_FILE_LABEL, supportedTypesText } from '@/utils/bulkContentUpload'

const files = (n: number) => `${n} ${inflect('file', n)}`
const Files = (n: number) => `${n} ${inflect('File', n)}`

type ModeCopy = {
  title: string
  subtitle: (moduleTitle?: string) => string
  steps: [string, string, string]
  helper: string
  selectNote: string
  reviewSubtitle: string
  reviewNote?: string
  titleColumn: string
  confirmLabel: (n: number) => string
  discardBody: string
}

// Courtney's Bulk Content Upload page, verbatim except where SME-1111 decisions 33 and 51 changed it.
export const bulkUploadCopy: Record<BulkUploadMode, ModeCopy> = {
  modules: {
    title: 'Upload Multiple Modules',
    subtitle: () => 'Each file you add becomes the primary file for a new content module.',
    steps: ['Select Files', 'Review Modules', 'Upload'],
    helper: 'Select as many files as you like, each one becomes its own module.',
    selectNote:
      "Nothing uploads yet. You will set each module's title, Sidekick, and delivery mode on the next step, then confirm to start the upload.",
    reviewSubtitle:
      'Check the title, Sidekick, and delivery mode for each file. You can change all three here, or later in Edit Module.',
    reviewNote:
      'Every module is created as a Draft as soon as its file finishes uploading. Nothing is visible to end users until you publish it.',
    titleColumn: 'Module Title',
    confirmLabel: n => `Confirm & Upload ${Files(n)}`,
    discardBody:
      'Nothing has uploaded yet. Closing this window discards the files you selected and no modules are created.',
  },
  supplementary: {
    title: 'Add Multiple Supplementary Files',
    subtitle: moduleTitle =>
      `These files will be added as supplementary content to ${moduleTitle?.trim() ? `"${moduleTitle.trim()}"` : 'this module'}.`,
    steps: ['Select Files', 'Review Files', 'Upload'],
    helper: "This module's primary file is not affected.",
    selectNote:
      "Nothing uploads yet. You will review each file's title on the next step, then confirm to start the upload.",
    reviewSubtitle: 'Check the title for each file.',
    titleColumn: 'File Title',
    confirmLabel: n => `Confirm & Add ${Files(n)}`,
    discardBody:
      'Nothing has uploaded yet. Closing this window discards the files you selected and nothing is added to this module.',
  },
}

export const dropzoneTitle = 'Drag files here, or click to browse'

export const sidekickDefaultLabel = 'Sidekick for all files'
export const modeDefaultLabel = 'Delivery mode for all files'
export const byFileTypeLabel = 'By file type'
export const customizedSummary = (n: number) => `${files(n)} customized`
export const resetAllLabel = 'Reset all'
export const resetAllDone = 'Every file follows the defaults again.'
export const resetToDefaultLabel = (value: string) => `Use the default (${value})`
export const mediaSkippedTooltip = "Media isn't available for this file type, so it uses the type's default."

export function defaultAppliedText(label: string, change: { applied: number; kept: number; skipped: number }) {
  const { applied, kept, skipped } = change
  // Nothing took the default when every file has its own pick, so the line leads with what was kept.
  return [
    applied > 0 &&
      (label === byFileTypeLabel
        ? `Delivery mode set by file type for ${files(applied)}`
        : `${label} set for ${files(applied)}`),
    skipped > 0 && `${files(skipped)} can't use Media`,
    kept > 0 && `${kept} customized ${inflect('file', kept)} kept`,
  ]
    .filter(Boolean)
    .join(' · ')
}
export const supportedTypesLabel = 'Supported types:'
export const discardTitle = 'Discard these files?'
export const allRemovedText = 'All files removed. Go back to select files.'

export const selectedSummary = (n: number, size: string) => `${files(n)} selected · ${size}`
export const reviewSummary = (n: number, size: string) => `${files(n)} · ${size}`

export const unsupportedWarning = (names: string[]) =>
  `Skipped ${names.length} unsupported ${inflect('file', names.length)}: ${names.join(', ')}. Supported types: ${supportedTypesText}.`

export const tooLargeWarning = (names: string[]) =>
  `Skipped ${files(names.length)} over the ${MAX_BULK_FILE_LABEL} limit: ${names.join(', ')}.`
